import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail("");
    }
  };

  return (
    <footer className="bg-[#3d1a0a] text-white mt-0">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-rose-600">
                <i className="ri-gift-line text-white text-lg"></i>
              </div>
              <span className="font-bold text-xl text-amber-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                Creative Hub
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              {t("footer_tagline")}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {[
                { icon: "ri-instagram-line", href: "#", title: "Instagram" },
                { icon: "ri-facebook-line", href: "#", title: "Facebook" },
                { icon: "ri-whatsapp-line", href: "https://wa.me/237678012159", title: "WhatsApp" },
                { icon: "ri-tiktok-line", href: "#", title: "TikTok" },
                { icon: "ri-store-2-line", href: "https://www.facebook.com/marketplace", title: "Facebook Marketplace" },
              ].map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.title}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-amber-600 text-gray-400 hover:text-white transition-all duration-200 cursor-pointer"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-amber-300 font-semibold text-sm uppercase tracking-widest mb-5">
              {t("footer_links")}
            </h4>
            <ul className="flex flex-col gap-3">
              {[
                { to: "/", label: t("nav_home") },
                { to: "/shop", label: t("nav_shop") },
                { to: "/custom-gift-box", label: t("nav_custom") },
                { to: "/occasions", label: t("nav_occasions") },
                { to: "/about", label: t("nav_about") },
                { to: "/blog", label: t("nav_blog") },
                { to: "/contact", label: t("nav_contact") },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-amber-300 text-sm transition-colors duration-200 cursor-pointer flex items-center gap-2 group"
                  >
                    <i className="ri-arrow-right-s-line text-amber-500 group-hover:translate-x-1 transition-transform duration-200"></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-amber-300 font-semibold text-sm uppercase tracking-widest mb-5">
              {t("footer_contact")}
            </h4>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-900/50 flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-amber-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-sm leading-relaxed">{t("contact_address")}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-900/50 flex-shrink-0">
                  <i className="ri-phone-line text-amber-400 text-sm"></i>
                </div>
                <a href="tel:+237678012159" className="text-gray-400 hover:text-amber-300 text-sm transition-colors cursor-pointer">
                  {t("contact_phone_val")}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-900/50 flex-shrink-0">
                  <i className="ri-mail-line text-amber-400 text-sm"></i>
                </div>
                <a href="mailto:creativehub@gmail.com" className="text-gray-400 hover:text-amber-300 text-sm transition-colors cursor-pointer">
                  {t("contact_email_val")}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-900/50 flex-shrink-0">
                  <i className="ri-time-line text-amber-400 text-sm"></i>
                </div>
                <p className="text-gray-400 text-sm">{t("contact_hours_val")}</p>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-amber-300 font-semibold text-sm uppercase tracking-widest mb-5">
              {t("footer_newsletter")}
            </h4>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              {t("footer_newsletter_desc")}
            </p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <i className="ri-checkbox-circle-line text-lg"></i>
                <span>Thank you for subscribing!</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer_newsletter_placeholder")}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  required
                />
                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-amber-600 to-rose-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap"
                >
                  {t("footer_newsletter_btn")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">{t("footer_rights")}</p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-gray-500 hover:text-amber-300 text-xs transition-colors cursor-pointer">
              {t("footer_privacy")}
            </a>
            <a href="#" className="text-gray-500 hover:text-amber-300 text-xs transition-colors cursor-pointer">
              {t("footer_terms")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}